import {
  Component,
  ElementRef,
  Input,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Hours, Step } from 'src/app/services/analyse/analyse.types';
import {
  Chart,
  ChartConfiguration,
  ChartDataset,
  registerables
} from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import 'chartjs-adapter-date-fns';
import { hasChanged } from 'src/app/utils/change-utils';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'app-daymax-graph',
  templateUrl: './daymaxGraph.component.html',
  styleUrls: ['./daymaxGraph.component.scss']
})
export class DaymaxGraphComponent {
  @ViewChild('chart') chartRef: ElementRef<HTMLCanvasElement>;
  @Input() allDayMax: Hours[];
  @Input() topThreeDayMax: Hours[];
  @Input() avg_high_three: number;
  @Input() steps: Step[]; // Alle nettleietrinn
  @Input() currentStep: Step;
  @Input() activeView: 'three' | 'month';
  chartId = 'daymax-chart';
  chart: Chart;
  dataSets: ChartDataset[] = [];
  labels: string[] = [];

  constructor(private platform: Platform) {}

  ngAfterViewInit() {
    Chart.register(...registerables, annotationPlugin);

    Chart.defaults.font.family = 'Plus Jakarta Sans';
    Chart.defaults.font.weight = 'bold';
    Chart.defaults.color = '#002987';
    Chart.defaults.datasets.bar.backgroundColor = '#002987';
    this.allDayMax = this.allDayMax.sort((a, b) => a.period - b.period);
    this.createChart();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (hasChanged([changes.allDayMax])) {
      this.allDayMax = changes.allDayMax.currentValue;
      this.createChart();
    }
  }

  findHighestStepToVisualize() {
    const max = Math.max(...this.dataSets[0].data.map((x) => x as number));
    const step = this.steps.find((x) => x.next_limit > max && x.step >= 3);
    return step?.next_limit ?? max;
  }

  createChart() {
    if (this.chart != null) {
      this.chart.destroy();
    }
    this.createChartDatasets();
    if (this.chartRef) {
      const ctx = this.chartRef.nativeElement;
      this.chart = new Chart(
        ctx,
        this.chartOptions(
          this.labels,
          this.dataSets,
          this.findHighestStepToVisualize()
        )
      );
    }
  }

  chartOptions = (
    labels: Date[] | string[],
    datasets: ChartDataset[],
    maxY: number
  ): ChartConfiguration => ({
    type: 'bar',
    data: {
      labels,
      datasets
    },
    options: {
      maintainAspectRatio: false,
      datasets: {
        bar: {
          borderRadius: 10,
          barThickness:
            this.activeView === 'month'
              ? this.platform.width() < 350 || new Date().getDate() > 20
                ? 4
                : 8
              : 20
        }
      },
      plugins: {
        annotation: {
          annotations: [
            {
              type: 'line',
              scaleID: 'y',
              value: this.avg_high_three,
              borderColor: '#002987',
              borderWidth: 2,
              borderDash: [5, 5]
            }
          ]
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          titleColor: '#002251',
          bodyColor: '#002251',
          footerColor: '#002251',
          mode: 'index',
          caretSize: 8,
          intersect: false,
          cornerRadius: 1,
          displayColors: false,
          yAlign: 'bottom',
          callbacks: {
            label: (ctx) => {
              return `Forbruk: ${ctx.formattedValue} kWh`;
            }
          }
        },
        legend: {
          position: 'bottom',
          labels: {
            pointStyle: 'circle',
            usePointStyle: true,
            generateLabels: (chart) => {
              if (this.activeView === 'month') {
                return [
                  {
                    text: 'Forbruk',
                    fillStyle: '#002987',
                    lineWidth: 0
                  },
                  {
                    text: 'Høyeste timer',
                    fillStyle: '#0a66ff',
                    lineWidth: 0
                  },
                  {
                    text: 'Grunnlag',
                    fillStyle: '#ffffff',
                    lineWidth: 2,
                    lineDash: [5, 5],
                    strokeStyle: '#002987'
                  }
                ];
              } else {
                return [
                  {
                    text: 'Høyeste timer',
                    fillStyle: '#0a66ff',
                    lineWidth: 0
                  },
                  {
                    text: 'Grunnlag',
                    fillStyle: '#ffffff',
                    lineWidth: 2,
                    lineDash: [5, 5],
                    strokeStyle: '#002987'
                  }
                ];
              }
            }
          },
          onClick: (e) => {
            return;
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          display: true,
          position: 'left',
          max: maxY,
          afterTickToLabelConversion: (ctx) => {
            ctx.ticks = [
              { value: 0, label: '0' },
              ...this.steps
                .filter(
                  (x) => x.next_limit <= this.findHighestStepToVisualize()
                )
                .map((x) => ({
                  value: x.next_limit,
                  label: x.next_limit + ' kWh'
                }))
            ];
          },
          grid: {
            display: true,
            color: '#002987'
          }
        },
        yCost: {
          display: true,
          position: 'right',
          max: maxY,
          afterTickToLabelConversion: (ctx) => {
            ctx.ticks = [
              //{ value: 0, label: '0' },
              ...this.steps
                .filter(
                  (x) => x.next_limit <= this.findHighestStepToVisualize()
                )
                .map((x) => ({
                  value: (x.next_limit - x.limit) / 2 + x.limit,
                  label: x.cost + ' kr'
                }))
            ];
          },
          grid: {
            display: false
          }
        }
      }
    }
  });

  createChartDatasets() {
    let colors = [];
    if (this.activeView === 'month') {
      colors = this.allDayMax?.map((x) =>
        x.kwh === this.topThreeDayMax[0].kwh ||
        x.kwh === this.topThreeDayMax[1].kwh ||
        x.kwh === this.topThreeDayMax[2].kwh
          ? '#0a66ff'
          : '#002987'
      );
    } else {
      colors = ['#0a66ff'];
    }
    this.dataSets = [
      {
        yAxisID: 'y',
        label: 'Forbruk',
        data: this.allDayMax?.map((x) => x.kwh),
        backgroundColor: colors
      }
    ];
    this.labels = this.allDayMax?.map((x) => {
      const p = x.period.toString();
      const date = new Date(
        +p.substring(0, 4),
        +p.substring(4, 6) - 1,
        +p.substring(6, 8)
      );

      return date.toLocaleDateString('nb-NO', {
        day: 'numeric',
        month: 'short'
      });
    });
  }
}
